import { createContext, CSSProperties, ReactNode, useContext, useState } from 'react';
import { Button, ButtonProps } from '@mui/material';
import BaseDialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { errorHandling } from 'src/utils/errorHandling';
import { useToastify } from './ToastContext';
import styled from 'styled-components';

import { GoAlert } from "react-icons/go"
import { LuTrash } from 'react-icons/lu';

interface Payload {
    title: string,
    message: string,
    icon: 'alert' | 'trash',
    size?: DialogProps['maxWidth'],
    actions: {
        label: string,
        variant?: ButtonProps['variant'],
        color?: ButtonProps['color'],
        onClick?: () => Promise<void> | void
    }[]
    sx?: {
        content?: CSSProperties
    }
}

const DialogContext = createContext<{
    showDialog: (payload: Payload) => Promise<void> | void
}
>({
    showDialog: () => { }
});

export const useDialog = () => useContext(DialogContext)

export const DialogProvider = (props: { children: ReactNode }) => {
    const toastr = useToastify();
    const [dialogContent, setDialogContent] = useState<Payload | undefined>();

    return (
        <DialogContext.Provider value={{
            showDialog: (payload: Payload) => setDialogContent({ ...payload, size: payload.size ?? "md" })
        }}>
            <BaseDialog
                open={!!dialogContent}
                onClose={() => setDialogContent(undefined)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth={dialogContent?.size}
            >
                {dialogContent?.icon && (
                    <ActionIcon>
                        {dialogContent?.icon === 'alert' ? <GoAlert />
                            : dialogContent?.icon === 'trash' ? <LuTrash />
                                : null}
                    </ActionIcon>
                )}
                <DialogTitle id="alert-dialog-title">
                    {dialogContent?.title}
                </DialogTitle>
                <DialogContent sx={dialogContent?.sx?.content}>
                    {dialogContent?.message}
                </DialogContent>
                {dialogContent?.actions && (
                    <DialogActions>
                        {dialogContent?.actions.map((action, index) => (
                            <Button
                                key={index}
                                variant={action.variant}
                                color={action.color}
                                onClick={async () => {
                                    try {
                                        setDialogContent(undefined);
                                        if (action.onClick)
                                            await action.onClick();
                                    }
                                    catch (err) {
                                        toastr.error(errorHandling(err));
                                    }
                                }}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </DialogActions>
                )}
            </BaseDialog>
            {props.children}
        </DialogContext.Provider>
    )
}

const ActionIcon = styled.div`
    background-color: ${({ theme }) => theme.palette.grey[900]};
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 2rem;
    color: ${({ theme }) => theme.palette.error.main};
`